import React, { createContext, useContext, useReducer } from 'react'
import * as Yup from 'yup'

import {
	EAuthSectionTypes,
	EAuthActionTypes,
	IAuthState,
	IAuthAction,
	IAuthProviderProps,
} from 'models'

const initialAuthState = {
	section: EAuthSectionTypes.login,
	temporaryUser: null,
	user: null,
	step: 0,
	values: {
		email: '',
		password: '',
		firstName: '',
		lastName: '',
	},
	validation: {
		email: false,
		password: false,
		firstName: false,
		lastName: false,
	},
	success: {
		signUp: false,
		activation: false,
	},
	error: {
		login: false,
		activation: false,
		forgotPassword: false,
	},
}
const AuthContext: React.Context<any> = createContext(null)
const reducer: React.Reducer<any, any> = (
	state: IAuthState,
	action: IAuthAction,
) => {
	switch (action.type) {
		case EAuthActionTypes.setTemporaryUser:
			return {
				...state,
				temporaryUser: action.user,
				validation: {
					...state.validation,
					email: true,
				},
			}
		case EAuthActionTypes.setSection:
			return {
				...state,
				section:
					(action.section && EAuthSectionTypes[action.section]) ||
					initialAuthState.section,
			}
		case EAuthActionTypes.incrementStep:
			return {
				...state,
				step: state.step + 1,
			}
		case EAuthActionTypes.decrementStep:
			return {
				...state,
				step: state.step - 1,
			}
		case EAuthActionTypes.setFirstName:
			return {
				...state,
				values: {
					...state.values,
					firstName: action.firstName,
				},
				validation: {
					...state.validation,
					firstName: Yup.string()
						.required('First Name is required')
						.max(50, 'First Name cannot exceed 50 characters')
						.isValidSync(action.firstName),
				},
			}
		case EAuthActionTypes.setLastName:
			return {
				...state,
				values: {
					...state.values,
					lastName: action.lastName,
				},
				validation: {
					...state.validation,
					lastName: Yup.string()
						.required('Last Name is required')
						.max(50, 'Last Name cannot exceed 50 characters')
						.isValidSync(action.lastName),
				},
			}
		case EAuthActionTypes.setEmail:
			return {
				...state,
				values: {
					...state.values,
					email: action.email,
				},
				validation: {
					...state.validation,
					email: Yup.string()
						.required('Email Address is required')
						.email('Email Address is not valid')
						.isValidSync(action.email),
				},
			}
		case EAuthActionTypes.setPassword:
			return {
				...state,
				values: {
					...state.values,
					password: action.password,
				},
				validation: {
					...state.validation,
					password: Yup.string()
						.required('Password is required')
						.isValidSync(action.password),
				},
			}
		case EAuthActionTypes.setSignUpSuccess:
			return {
				...state,
				section: EAuthSectionTypes.login,
				values: {
					...initialAuthState.values,
				},
				validation: {
					...initialAuthState.validation,
				},
				error: {
					...initialAuthState.error,
				},
				success: {
					...initialAuthState.success,
					signUp: true,
				},
			}
		case EAuthActionTypes.setActivationSuccess:
			return {
				...state,
				values: {
					...initialAuthState.values,
				},
				validation: {
					...initialAuthState.validation,
				},
				error: {
					...initialAuthState.error,
				},
				success: {
					...initialAuthState.success,
					activation: true,
				},
			}
		case EAuthActionTypes.setActivationError:
			return {
				...state,
				error: {
					...initialAuthState.error,
					activation: true,
				},
			}
		case EAuthActionTypes.setForgotPasswordSuccess:
			return {
				...state,
				values: {
					...initialAuthState.values,
				},
				validation: {
					...initialAuthState.validation,
				},
				error: {
					...initialAuthState.error,
				},
				success: {
					...initialAuthState.success,
					forgotPassword: true,
				},
			}
		case EAuthActionTypes.setForgotPasswordFailed:
			return {
				...state,
				error: {
					...initialAuthState.error,
					forgotPassword: true,
				},
			}
		case EAuthActionTypes.setUpdateForgotPasswordSuccess:
			return {
				...state,
				values: {
					...initialAuthState.values,
				},
				validation: {
					...initialAuthState.validation,
				},
				error: {
					...initialAuthState.error,
				},
				success: {
					...initialAuthState.success,
					updateForgotPassword: true,
				},
			}
		case EAuthActionTypes.setUpdateForgotPasswordFailed:
			return {
				...state,
				error: {
					...initialAuthState.error,
					updateForgotPassword: true,
				},
			}
		case EAuthActionTypes.authFailed:
			return {
				...state,
				error: {
					...initialAuthState.error,
					login: true,
				},
			}
		default:
			throw Error('Invalid Authentication action')
	}
}
export const AuthProvider: React.FunctionComponent<IAuthProviderProps> = ({
	children,
	initialSection,
}) => (
	<AuthContext.Provider
		value={useReducer(reducer, {
			...initialAuthState,
			...{
				section: initialSection
					? EAuthSectionTypes[initialSection]
					: EAuthSectionTypes.login,
			},
		})}
	>
		{children}
	</AuthContext.Provider>
)

// new version of store
interface IAuthStore {
	children: any
	keys: any
	// TODO: Update the interface with proper types
}

export const AuthStore = ({ children, keys = [] }: IAuthStore) => {
	let [state, dispatch] = useContext(AuthContext)
	const keysMap: any = {}
	keys?.map((item: any) => {
		// const temp =
		// 	item.indexOf("|") === -1
		// 		? item.split(".")
		// 		: item.split("|")[0].split(".");
		// if (temp.length === 1)
		// 	keysMap[item] = item;
		// else
		keysMap[item] =
			item.indexOf('|') === -1
				? item.split('.')
				: item.split('|')[0].split('.')
		return undefined
	})
	let result: { [key: string]: any } = []
	keys?.map((item: any) => {
		const last =
			item.indexOf('|') === -1
				? [...keysMap[item]]?.reverse()[0]
				: item.split('|')[1]
		if (typeof keysMap[item] === 'object') {
			result[last] = keysMap[item]?.reduce(
				(a: any, c: any) => (a !== null ? a[c] : null),
				state,
			)
		} else {
			result[last] = state[keysMap[item]]
		}
		return undefined
	})

	if (keys.indexOf('dispatch') !== -1) result['dispatch'] = dispatch

	// TODO: update with a proper interface.
	let elements: any = React.Children.toArray(children)
	if (elements.length === 1) {
		elements = React.cloneElement(elements[0], result)
	} else if (elements.length > 0) {
		for (let i in elements) {
			elements[i] = React.cloneElement(elements[i], result)
		}
	}
	return <>{elements}</>
}

export const DataContext = (keys: string[]) => {
	const state = useContext(AuthContext)
	return Object.keys(state[0])
		.filter(key => keys.indexOf(key) !== -1)
		.reduce((accumulator: any, current) => {
			accumulator[current] = state[0][current]
			return accumulator
		}, {})
}

export const useAuthStateTest = (Component: any, keys: any) => {
	return (props: any) => <Component {...DataContext(keys)} {...props} />
}

export const useAuthState = () => useContext(AuthContext)
